import type { App } from 'vue'

import { createApp } from '@app/adapters/create-app'
import LiveInputBubble from '@app/application/communication/sections/communication/components/remarks/live-input-bubble.vue'

interface BubbleHandler {
  buildBubble: ({
    liquidInputFieldPath,
  }: {
    liquidInputFieldPath: string
  }) => App<Element> | null
  insertWrapper: (
    parent: HTMLElement,
    otherElements: Array<HTMLElement>
  ) => void
}

export default (element: Element, isPreviewMode: boolean): BubbleHandler => {
  const buildBubble = ({
    liquidInputFieldPath,
  }: {
    liquidInputFieldPath: string
  }): App<Element> | null => {
    const targetElement = buildTargetElement() as HTMLElement

    if (!targetElement) {
      return null
    }

    const newBubble = createApp(LiveInputBubble, {
      linkingPath: liquidInputFieldPath,
      isPreviewMode: isPreviewMode,
    })

    newBubble.mount(targetElement)

    return newBubble
  }

  const buildTargetElement = (): ChildNode | null => {
    const parent = element.parentNode as HTMLElement

    if (shouldManipulateParent(parent)) {
      insertPlaceholder(parent)
    }

    return element.previousSibling
  }

  const shouldManipulateParent = (parent?: HTMLElement): boolean => {
    return !parent ? false : !parent.classList?.contains('sf-markdown-group')
  }

  const insertPlaceholder = (parent: HTMLElement): void => {
    const bubblePlaceholder = document.createElement('div')

    // Prevent the bubble from wrapping the select element inline as it throws off the caret position
    if (element.tagName !== 'SELECT') {
      bubblePlaceholder.classList.add('tw-inline-block')
    }

    bubblePlaceholder.setAttribute('data-behaviour', 'bubble-placeholder')
    parent.insertBefore(bubblePlaceholder, element)
  }

  const insertWrapper = (
    parent: HTMLElement,
    elementsToMove: Array<HTMLElement>
  ): void => {
    if (!shouldManipulateParent(parent)) {
      return
    }

    // This is a workaround to make the UI kit design work in the old rendered liquid design
    // TODO: remove this once we're on the new design with recon texts

    const extraPreviewClasses = isPreviewMode
      ? 'sf-inline-block sf-pad-r-1 js-preview-mode'
      : 'sf-inline-block'

    const wrapperDiv = document.createElement('div')
    wrapperDiv.className = `sf-markdown-group ${extraPreviewClasses}`
    wrapperDiv.dataset.testid = 'bubble-wrapper'

    parent.insertBefore(wrapperDiv, element)

    elementsToMove.forEach((child) => {
      wrapperDiv.appendChild(child)
    })
  }

  return {
    buildBubble,
    insertWrapper,
  }
}
